import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import companyService from "@services/company.service";
import useCompanyStore from "@store/useCompanyStore";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useCreateMeetingObject } from "@screens/meeting/hooks/useCreateMeetingObject";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { CreateCompanyRequest } from "@app-types/requests.type";
import {
  COMPANIES_QUERY_KEY,
  COMPANY_QUERY_KEY,
  CREATE_COMPANY_MUTATION_KEY,
  FIND_COMPANY_BY_WEBSITE_QUERY_KEY,
} from "@config/query";
import { RelationObjectType } from "@enums/relations.enum";

export function useCreateCompany(contentType: RelationObjectType, onSuccess?: VoidFunction) {
  const { setCompanyId } = useCompanyStore();
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { mutateAsync: createMeetingObject } = useCreateMeetingObject();

  return useMutation({
    mutationKey: [CREATE_COMPANY_MUTATION_KEY],
    mutationFn: async (data: CreateCompanyRequest) => {
      return await companyService.createCompany(data);
    },
    onSuccess: async (response) => {
      onSuccess?.();
      queryClient.setQueryData([COMPANY_QUERY_KEY, response.id], response);
      void queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
      // Wait for the URL-based company lookup to confirm the company is fetchable
      // before setting companyId. This ensures "View in Meridian" only becomes
      // clickable once the CRM can actually serve the company page.
      await queryClient.invalidateQueries({ queryKey: [FIND_COMPANY_BY_WEBSITE_QUERY_KEY] });
      setCompanyId(response.id);

      if (contentType === RelationObjectType.MEETING) {
        void createMeetingObject({
          object_type: RelationObjectType.COMPANY,
          object_id: response.id,
        });
      }

      ToastService.showSuccess(`"${response.title}" company created`);
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.CREATE_COMPANY);
      ToastService.showRejection(error as ApiError, ERROR_MESSAGES.CREATE_COMPANY);
    },
  });
}
